import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  tabsConfig,
  cabinaMensajes,
  cabinaEncuestas,
  cabinaConcursos,
  cabinaTopFans,
  cabinaStats,
  moderacionConfig,
  locutorActual,
  programaActual,
  reglasCabina,
} from '@/mocks/cabina2';
import type { CabinaTab } from '@/mocks/cabina2';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  return `Hace ${Math.floor(mins / 60)}h`;
}

const typeBadge: Record<string, { label: string; bg: string; text: string }> = {
  mensaje: { label: 'Mensaje', bg: 'bg-foreground-100', text: 'text-foreground-600' },
  saludo: { label: 'Saludo', bg: 'bg-rose-100', text: 'text-rose-700' },
  cancion: { label: 'Canción', bg: 'bg-violet-100', text: 'text-violet-700' },
  voz: { label: 'Voz', bg: 'bg-sky-100', text: 'text-sky-700' },
  concurso_respuesta: { label: 'Concurso', bg: 'bg-amber-100', text: 'text-amber-700' },
  encuesta_voto: { label: 'Encuesta', bg: 'bg-emerald-100', text: 'text-emerald-700' },
};

export default function Cabina2Section() {
  const [activeTab, setActiveTab] = useState<CabinaTab>('chat');

  const mensajesFiltrados = cabinaMensajes.filter((m) => {
    if (activeTab === 'chat') return m.type === 'mensaje' || m.type === 'voz';
    if (activeTab === 'saludos') return m.type === 'saludo';
    if (activeTab === 'canciones') return m.type === 'cancion';
    if (activeTab === 'concursos') return m.type === 'concurso_respuesta';
    if (activeTab === 'encuestas') return m.type === 'encuesta_voto';
    return true;
  });

  const tabCounts: Record<CabinaTab, number> = {
    chat: cabinaMensajes.filter((m) => m.type === 'mensaje' || m.type === 'voz').length,
    saludos: cabinaMensajes.filter((m) => m.type === 'saludo').length,
    canciones: cabinaMensajes.filter((m) => m.type === 'cancion').length,
    encuestas: cabinaEncuestas.filter((e) => e.activa).length,
    concursos: cabinaConcursos.filter((c) => c.activo).length,
    'top-fans': cabinaTopFans.length,
  };

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            CABINA 2.0
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            La Cabina es el Corazón de RFW
          </h2>
          <p className="text-foreground-500 text-sm max-w-[620px] mx-auto leading-relaxed">
            Chat, saludos, canciones, encuestas, concursos y top fans en un solo lugar. Moderación completa con atajos de teclado. Locutor, oyentes VIP y embajadores visibles siempre.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Oyentes Ahora', valor: cabinaStats.oyentesAhora.toLocaleString(), icono: 'ri-user-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { label: 'Mensajes Hoy', valor: cabinaStats.mensajesHoy.toString(), icono: 'ri-chat-3-line', color: 'text-violet-600', bg: 'bg-violet-100' },
            { label: 'VIPs en Cabina', valor: cabinaStats.vipsEnCabina.toString(), icono: 'ri-vip-crown-line', color: 'text-amber-600', bg: 'bg-amber-100' },
            { label: 'Mensajes/min', valor: cabinaStats.mensajesPorMinuto.toString(), icono: 'ri-timer-flash-line', color: 'text-sky-600', bg: 'bg-sky-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Locutor + Programa bar */}
        <div className="bg-background-50 rounded-2xl border border-background-200 p-5 mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <img
                src={locutorActual.avatar}
                alt={locutorActual.nombre}
                className="w-14 h-14 rounded-2xl object-cover"
                loading="lazy"
              />
              <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                <span className="w-2 h-2 bg-background-50 rounded-full animate-pulse"></span>
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-foreground-950 font-bold text-sm">{locutorActual.nombre}</p>
                <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold">{locutorActual.estado}</span>
              </div>
              <p className="text-foreground-500 text-xs">{locutorActual.programa} &bull; {programaActual.radio}</p>
              <p className="text-foreground-400 text-[10px]">{programaActual.horario} &bull; {programaActual.oyentes.toLocaleString()} oyentes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-semibold flex items-center gap-1">
              <i className="ri-vip-crown-line"></i>{cabinaStats.vipsEnCabina} VIP
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-[10px] font-semibold flex items-center gap-1">
              <i className="ri-shield-star-line"></i>{cabinaStats.embajadoresEnCabina} Embajadores
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 mb-6 flex-wrap">
          {tabsConfig.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
              }`}
            >
              <i className={tab.icono}></i>
              {tab.label}
              <span className={`text-[10px] ml-0.5 ${activeTab === tab.id ? 'text-background-50/70' : 'text-foreground-400'}`}>
                {tabCounts[tab.id]}
              </span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="space-y-4">
          {activeTab === 'top-fans' ? (
            /* Top Fans View */
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {cabinaTopFans.slice(0, 8).map((fan, idx) => (
                <div key={fan.id} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
                  <div className="relative inline-block mb-3">
                    <img src={fan.avatar} alt={fan.nombre} className="w-14 h-14 rounded-2xl object-cover" loading="lazy" />
                    <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-background-50 border-2 border-background-200 flex items-center justify-center text-[10px] font-bold text-foreground-950">
                      {idx + 1}
                    </span>
                  </div>
                  <p className="text-foreground-950 font-bold text-xs">{fan.nombre}</p>
                  <p className="text-foreground-500 text-[10px]">{fan.nivel}</p>
                  <p className="text-foreground-400 text-[9px]">{fan.mensajes.toLocaleString()} msgs &bull; {fan.racha}d racha</p>
                </div>
              ))}
            </div>
          ) : activeTab === 'encuestas' ? (
            /* Encuestas View */
            <div className="space-y-4">
              {cabinaEncuestas.filter((e) => e.activa).map((enc) => (
                <div key={enc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-foreground-950 font-bold text-sm">{enc.pregunta}</p>
                    <span className="text-emerald-600 text-[10px] font-semibold bg-emerald-100 px-2 py-0.5 rounded-md">
                      {enc.tiempoRestante}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {enc.opciones.map((op) => {
                      const pct = enc.totalVotos > 0 ? Math.round((op.votos / enc.totalVotos) * 100) : 0;
                      return (
                        <div key={op.id} className="relative">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-foreground-600">{op.texto}</span>
                            <span className="text-foreground-400">{pct}%</span>
                          </div>
                          <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                            <div className="h-full bg-accent-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-foreground-400 text-[10px] mt-3">{enc.totalVotos.toLocaleString()} votos totales</p>
                </div>
              ))}
            </div>
          ) : activeTab === 'concursos' ? (
            /* Concursos View */
            <div className="space-y-4">
              {cabinaConcursos.filter((c) => c.activo).map((con) => (
                <div key={con.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-foreground-950 font-bold text-sm">{con.titulo}</p>
                      <p className="text-amber-600 text-xs font-semibold flex items-center gap-1">
                        <i className="ri-trophy-line"></i>{con.premio}
                      </p>
                    </div>
                    <span className="text-emerald-600 text-[10px] font-semibold bg-emerald-100 px-2 py-0.5 rounded-md">
                      {con.tiempoRestante}
                    </span>
                  </div>
                  <p className="text-foreground-500 text-xs mb-2">Pregunta: "{con.pregunta}"</p>
                  <p className="text-foreground-400 text-[10px]">{con.respuestas.length} respuestas recibidas</p>
                </div>
              ))}
            </div>
          ) : (
            /* Message-based tabs */
            mensajesFiltrados.slice(0, 6).map((msg) => {
              const badge = typeBadge[msg.type];
              return (
                <div key={msg.id} className={`bg-background-50 rounded-2xl border p-4 ${msg.destacado ? 'border-amber-300 bg-amber-50/30' : 'border-background-200'} ${msg.bloqueado ? 'opacity-40' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <img src={msg.senderAvatar} alt={msg.senderName} className="w-10 h-10 rounded-xl object-cover" loading="lazy" />
                      {msg.esEmbajador && (
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-violet-500 rounded-full border border-background-50 flex items-center justify-center">
                          <i className="ri-shield-star-line text-background-50 text-[7px]"></i>
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-foreground-950 font-bold text-xs">{msg.senderName}</span>
                        {msg.senderBadge && (
                          <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                            msg.senderBadge.includes('VIP') ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                          }`}>
                            {msg.senderBadge}
                          </span>
                        )}
                        <span className={`px-1.5 py-0.5 rounded-md ${badge.bg} ${badge.text} text-[9px] font-semibold`}>
                          {badge.label}
                        </span>
                        {msg.destacado && (
                          <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold">
                            Destacado
                          </span>
                        )}
                        {msg.bloqueado && (
                          <span className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 text-[9px] font-semibold">
                            Bloqueado
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-600 text-xs leading-relaxed">{msg.content}</p>
                      {msg.songRequest && (
                        <p className="text-violet-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                          <i className="ri-music-line"></i>{msg.songRequest}
                        </p>
                      )}
                    </div>
                    <span className="text-foreground-400 text-[10px] shrink-0">{timeAgo(msg.timestamp)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Moderación preview */}
        <div className="mt-8">
          <h4 className="text-foreground-950 font-bold text-sm mb-4 text-center">Moderación al Instante</h4>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {moderacionConfig.slice(0, 4).map((acc) => (
              <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 px-4 py-3 text-center min-w-[90px]">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-1.5`}>
                  <i className={`${acc.icono} text-background-50 text-sm`}></i>
                </div>
                <p className="text-foreground-950 font-bold text-[10px]">{acc.label}</p>
                <span className="text-foreground-400 text-[9px] font-mono">Tecla {acc.shortcut}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reglas */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {reglasCabina.slice(0, 2).map((regla) => (
            <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                  <i className={`${regla.icono} text-background-50 text-sm`}></i>
                </div>
                <div>
                  <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/cabina-2"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-broadcast-line"></i>
            Entrar a la Cabina 2.0
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            "Nada de botones duplicados fuera de Cabina. La Cabina es el único centro de comando."
          </p>
        </div>
      </div>
    </section>
  );
}